import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { Map as ImmutableMap } from 'immutable';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import SettingsTextField from '@sophocles/views/settings/components/SettingsTextField';

// V2 modern Special Instruction add/edit dialog.  Replaces the
// legacy Formsy-based `SpecialInstructionDialog` for the V2 site
// group settings surface.  Caller wires up the underlying CRUD
// thunks — this component is presentation-only.
//
// Props:
//   open          : boolean — render or not
//   instructionId : number | null — null for "new", number for "edit"
//   onClose       : () => void
//   onCreate      : (values) => Promise<{error?, payload?}> — fired for new
//   onUpdate      : (id, values) => Promise<{error?, payload?}> — fired for edit
//   onFetch       : (id) => Promise<Immutable.Map | object> — load full
//                   instruction (read-only created/updated metadata)
//   onSaved       : () => void — called after a successful save

const APPLIES_TO_OPTIONS = [
  { value: 1, key: 'rma' },
  { value: 2, key: 'orders' },
  { value: 3, key: 'rmaAndOrders' },
];

const validationSchema = yup.object({
  applies_to: yup.number().integer().required(),
  instructions: yup.string().trim().required('Instructions are required.'),
});

const formatRelative = ts => {
  if (!ts) return null;
  const m = moment(ts);
  return { rel: m.fromNow(), abs: m.format('L, LTS') };
};

const userDisplay = user => {
  if (!user) return null;
  const get = ImmutableMap.isMap(user) ? key => user.get(key) : key => user[key];
  const first = get('first_name') ?? '';
  const last = get('last_name') ?? '';
  return [first, last].filter(Boolean).join(' ').trim() || get('username') || 'Unknown';
};

const getField = (obj, key, fallback = undefined) => {
  if (obj == null) return fallback;
  if (ImmutableMap.isMap(obj)) return obj.get(key, fallback);
  return obj[key] ?? fallback;
};

export default function SpecialInstructionDialog({
  open,
  instructionId,
  onClose,
  onCreate,
  onUpdate,
  onFetch,
  onSaved,
}) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = useState(null);
  const isEdit = instructionId != null;

  useEffect(() => {
    if (!open || !isEdit || !onFetch) return undefined;
    let cancelled = false;
    onFetch(instructionId).then(res => {
      if (cancelled) return;
      if (res && !res.error) setInstruction(res);
    });
    return () => {
      cancelled = true;
    };
  }, [open, instructionId, isEdit, onFetch]);

  const initialValues = {
    applies_to: getField(instruction, 'applies_to', 1),
    instructions: getField(instruction, 'instructions', ''),
  };

  const handleSubmit = async values => {
    const result = isEdit ? await onUpdate(instructionId, values) : await onCreate(values);
    if (result?.error) return;
    onSaved?.();
  };

  const updatedBy = getField(instruction, 'updated_by');
  const createdBy = getField(instruction, 'created_by');
  const updated = formatRelative(getField(instruction, 'updated'));
  const created = formatRelative(getField(instruction, 'created'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogTitle>
              {isEdit
                ? t('specialInstructions.editTitle', 'Edit Special Instruction')
                : t('specialInstructions.newTitle', 'New Special Instruction')}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5} sx={{ pt: 1 }}>
                <Field name="applies_to">
                  {({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="applies-to-label">{t('general.category', 'Category')}</InputLabel>
                      <Select {...field} labelId="applies-to-label" label={t('general.category', 'Category')}>
                        {APPLIES_TO_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {t(`specialInstructions.appliesTo.${opt.key}`, defaultAppliesTo(opt.key))}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Field>
                <SettingsTextField
                  name="instructions"
                  label={t('specialInstructions.instruction', 'Instruction')}
                  multiline
                  minRows={4}
                  size="small"
                  required
                />
                {(updatedBy || createdBy) && (
                  <Stack spacing={0.5} sx={{ pt: 1 }}>
                    {updatedBy && (
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {t('general.updated', 'Updated')}:
                        </Box>{' '}
                        {userDisplay(updatedBy)}
                        {updated ? (
                          <>
                            {' — '}
                            <span title={updated.abs}>{updated.rel}</span>
                          </>
                        ) : null}
                      </Typography>
                    )}
                    {createdBy && (
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {t('general.created', 'Created')}:
                        </Box>{' '}
                        {userDisplay(createdBy)}
                        {created ? (
                          <>
                            {' — '}
                            <span title={created.abs}>{created.rel}</span>
                          </>
                        ) : null}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={submitForm}
                disabled={!isValid || isSubmitting || (isEdit && !dirty)}
              >
                {t('actions.save', 'Save')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function defaultAppliesTo(key) {
  switch (key) {
    case 'rma':
      return 'RMA';
    case 'orders':
      return 'Orders';
    case 'rmaAndOrders':
      return 'RMA and Orders';
    default:
      return key;
  }
}
